import React, { useState } from 'react';
import { Wind, DollarSign, Home, AlertCircle, ArrowRight, X, Download, Share2, Settings } from 'lucide-react';
import { getProductDatabase } from './data/productUtils';
import AdminConsole from './AdminConsole';

const AirPurifierAgent = () => {
  const [showAdmin, setShowAdmin] = useState(false);
  const [step, setStep] = useState(1);
  const [criteria, setCriteria] = useState({
    roomSize: '',
    budget: '',
    concerns: [],
    features: [],
    strictBudget: true,
    strictRoomSize: true,
    strictConcerns: true
  });
  const [recommendations, setRecommendations] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [compareList, setCompareList] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [sortBy, setSortBy] = useState('match');
  const [currentPage, setCurrentPage] = useState(1);
  const resultsPerPage = 3;

  // Load product database from JSON file
  const purifierDatabase = getProductDatabase();

  const concerns = [
    { id: "allergies", label: "Allergies & Pollen", icon: "🌸" },
    { id: "pets", label: "Pet Dander & Odors", icon: "🐕" },
    { id: "smoke", label: "Smoke & Odors", icon: "🚬" },
    { id: "dust", label: "Dust", icon: "💨" },
    { id: "voc", label: "VOCs & Chemicals", icon: "⚗️" },
    { id: "mold", label: "Mold", icon: "🍄" },
    { id: "odor", label: "Odors", icon: "👃" },
    { id: "germs", label: "Germs & Viruses", icon: "🦠" }
  ];

  const features = [
    { id: "smart", label: "Smart Features & App", icon: "📱" },
    { id: "quiet", label: "Very Quiet Operation", icon: "🔇" },
    { id: "low-maintenance", label: "Low Maintenance", icon: "🔧" },
    { id: "energy-efficient", label: "Energy Efficient", icon: "⚡" }
  ];

  const handleCriteriaChange = (field, value) => {
    setCriteria(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayValue = (field, value) => {
    setCriteria(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const calculateScore = (purifier) => {
    let score = 0;
    let matchDetails = [];

    if (criteria.roomSize) {
      const roomSize = parseInt(criteria.roomSize);
      if (purifier.roomSize >= roomSize) {
        const coverage = purifier.roomSize / roomSize;
        if (coverage >= 1 && coverage <= 1.5) {
          score += 30;
          matchDetails.push("Perfect room size coverage");
        } else if (coverage > 1.5 && coverage <= 2) {
          score += 28;
          matchDetails.push("Excellent coverage with room to spare");
        } else if (coverage > 2) {
          score += 25;
          matchDetails.push("Covers much larger area than needed");
        }
      } else {
        const deficit = ((roomSize - purifier.roomSize) / roomSize) * 100;
        if (deficit < 20) {
          score += 15;
          matchDetails.push("Slightly undersized but may work");
        } else {
          score += 5;
          matchDetails.push("May be undersized for your room");
        }
      }
    }

    if (criteria.budget) {
      const budget = parseInt(criteria.budget);
      if (purifier.price <= budget) {
        const ratio = purifier.price / budget;
        score += Math.round(25 * ratio);
        matchDetails.push(`Within budget ($${purifier.price})`);
      } else {
        const overBudget = purifier.price - budget;
        if (overBudget <= 100) {
          score += 10;
          matchDetails.push(`Slightly over budget by $${overBudget}`);
        } else {
          matchDetails.push(`Over budget by $${overBudget}`);
        }
      }
    }

    if (criteria.concerns.length > 0) {
      const concernMatches = criteria.concerns.filter(c => purifier.bestFor.includes(c));
      if (concernMatches.length > 0) {
        score += (concernMatches.length / criteria.concerns.length) * 30;
        matchDetails.push(`Addresses ${concernMatches.length} of ${criteria.concerns.length} concerns`);
      }
    }

    let featurePoints = 0;
    const rawFeatures = purifier._rawFeatures || {};
    
    // Smart Features: Check for app_control in raw features
    if (criteria.features.includes('smart') && rawFeatures.smart_features?.app_control === true) {
      featurePoints += 5;
      matchDetails.push("Has smart features");
    }
    
    // Quiet Operation: Check quiet_operation in raw features
    if (criteria.features.includes('quiet') && rawFeatures.quiet_operation === true) {
      featurePoints += 5;
      matchDetails.push("Quiet operation");
    }
    
    // Low Maintenance: Check easy_maintenance in raw features
    if (criteria.features.includes('low-maintenance') && rawFeatures.maintenance?.easy_maintenance === true) {
      featurePoints += 5;
      matchDetails.push("Low maintenance");
    }
    
    // Energy Efficient: Check energy_star_certified in raw features
    if (criteria.features.includes('energy-efficient') && rawFeatures.energy_star_certified === true) {
      featurePoints += 5;
      matchDetails.push("Energy efficient");
    }
    
    score += featurePoints;

    return { score: Math.round(score), matchDetails };
  };

  const generateRecommendations = () => {
    console.log('=== Generate Recommendations ===');
    console.log('Criteria:', criteria);
    console.log('Total products in database:', purifierDatabase.length);
    
    let scored = purifierDatabase.map(purifier => {
      const { score, matchDetails } = calculateScore(purifier);
      return { ...purifier, score, matchDetails };
    });
    console.log('After scoring:', scored.length);

    // Apply strict room size filter if enabled
    if (criteria.strictRoomSize && criteria.roomSize) {
      const roomSize = parseInt(criteria.roomSize);
      console.log('Applying strict room size filter:', roomSize, 'sq ft');
      const beforeFilter = scored.length;
      
      // For very large rooms (1500+ sq ft), show ALL purifiers that can cover it (no upper limit)
      // For smaller rooms, use 1.5x cap to avoid oversized units
      const useUpperLimit = roomSize < 1500;
      const maxCoverage = useUpperLimit ? roomSize * 1.5 : Infinity;
      
      scored = scored.filter(product => {
        const passes = product.roomSize >= roomSize && product.roomSize <= maxCoverage;
        if (!passes) {
          if (product.roomSize < roomSize) {
            console.log(`Filtered out: ${product.name} (${product.roomSize} sq ft < ${roomSize} sq ft - too small)`);
          } else if (useUpperLimit) {
            console.log(`Filtered out: ${product.name} (${product.roomSize} sq ft > ${maxCoverage} sq ft - oversized)`);
          }
        }
        return passes;
      });
      
      const rangeText = useUpperLimit 
        ? `${roomSize}-${maxCoverage} sq ft` 
        : `${roomSize}+ sq ft (no upper limit)`;
      console.log(`Room size filter: ${beforeFilter} → ${scored.length} products (${rangeText})`);
    }

    // Apply strict budget filter if enabled
    if (criteria.strictBudget && criteria.budget) {
      const budgetLimit = parseInt(criteria.budget);
      const minBudget = Math.round(budgetLimit * 0.5); // Set minimum at 50% of budget
      const maxBudget = Math.round(budgetLimit * 1.1); // Allow 10% over budget
      console.log('Applying strict budget filter: $', minBudget, '-', maxBudget);
      const beforeFilter = scored.length;
      // Show products within budget range (50% to 110% of budget)
      scored = scored.filter(product => {
        const passes = product.price >= minBudget && product.price <= maxBudget;
        if (!passes) {
          if (product.price < minBudget) {
            console.log(`Filtered out: ${product.name} ($${product.price} < $${minBudget} - too cheap)`);
          } else {
            console.log(`Filtered out: ${product.name} ($${product.price} > $${maxBudget} - over budget)`);
          }
        }
        return passes;
      });
      console.log(`Budget filter: ${beforeFilter} → ${scored.length} products ($${minBudget}-$${maxBudget})`);
    }

    // Apply strict concerns filter if enabled
    if (criteria.strictConcerns && criteria.concerns.length > 0) {
      console.log('Applying strict concerns filter:', criteria.concerns);
      const beforeFilter = scored.length;
      // Show only products that match at least one concern
      scored = scored.filter(product => {
        const concernMatches = criteria.concerns.filter(c => product.bestFor.includes(c));
        const passes = concernMatches.length > 0;
        if (!passes) {
          console.log(`Filtered out: ${product.name} (doesn't address any selected concerns)`);
        }
        return passes;
      });
      console.log(`Concerns filter: ${beforeFilter} → ${scored.length} products`);
    }

    // Apply strict features filter if any features are selected
    if (criteria.features.length > 0) {
      console.log('Applying features filter:', criteria.features);
      const beforeFilter = scored.length;
      
      scored = scored.filter(product => {
        const rawFeatures = product._rawFeatures || {};
        let passes = true;
        
        // Check each selected feature - ALL must match (strict AND logic)
        if (criteria.features.includes('smart')) {
          const hasFeature = rawFeatures.smart_features?.app_control === true;
          if (!hasFeature) {
            console.log(`Filtered out: ${product.name} (missing smart features)`);
            passes = false;
          }
        }
        
        if (criteria.features.includes('quiet') && passes) {
          const hasFeature = rawFeatures.quiet_operation === true;
          if (!hasFeature) {
            console.log(`Filtered out: ${product.name} (not quiet operation)`);
            passes = false;
          }
        }
        
        if (criteria.features.includes('low-maintenance') && passes) {
          const hasFeature = rawFeatures.maintenance?.easy_maintenance === true;
          if (!hasFeature) {
            console.log(`Filtered out: ${product.name} (not low maintenance)`);
            passes = false;
          }
        }
        
        if (criteria.features.includes('energy-efficient') && passes) {
          const hasFeature = rawFeatures.energy_star_certified === true;
          if (!hasFeature) {
            console.log(`Filtered out: ${product.name} (not energy efficient)`);
            passes = false;
          }
        }
        
        return passes;
      });
      
      console.log(`Features filter: ${beforeFilter} → ${scored.length} products (selected: ${criteria.features.join(', ')})`);
    }

    // Filter out products with low match scores (below 40%)
    const filtered = scored.filter(product => product.score >= 40);
    console.log('After 40% score filter:', filtered.length);
    
    // If no products meet the threshold, show top 3 anyway with a warning
    const final = filtered.length > 0 ? filtered : scored.slice(0, 3);
    console.log('Final results:', final.length);
    
    const sorted = final.sort((a, b) => b.score - a.score);
    console.log('Sorted results:', sorted.map(p => `${p.name} (${p.roomSize} sq ft, $${p.price}, ${p.score}%)`));
    setRecommendations(sorted);
    setCurrentPage(1); // Reset to first page when new recommendations are generated
    setStep(3);
  };

  const resetAgent = () => {
    setStep(1);
    setCriteria({
      roomSize: '',
      budget: '',
      concerns: [],
      features: [],
      strictBudget: true,
      strictRoomSize: true,
      strictConcerns: false
    });
    setRecommendations([]);
    setSelectedProduct(null);
    setCompareList([]);
    setShowComparison(false);
    setSortBy('match');
    setCurrentPage(1);
  };

  const toggleCompare = (product) => {
    if (compareList.find(p => p.name === product.name)) {
      setCompareList(compareList.filter(p => p.name !== product.name));
    } else if (compareList.length < 3) {
      setCompareList([...compareList, product]);
    }
  };

  const calculateYearlyCost = (product) => {
    const filterCostPerYear = product.filterLifespan.includes('month')
      ? (12 / parseInt(product.filterLifespan)) * product.filterCost
      : product.filterCost;
   
    const energyCostPerYear = (product.energyUsage * 8 * 365 * 0.13) / 1000;
   
    return Math.round(filterCostPerYear + energyCostPerYear);
  };

  const sortRecommendations = (recs) => {
    const sorted = [...recs];
    switch(sortBy) {
      case 'match':
        return sorted.sort((a, b) => b.score - a.score);
      case 'price-low':
        return sorted.sort((a, b) => a.price - b.price);
      case 'price-high':
        return sorted.sort((a, b) => b.price - a.price);
      case 'rating':
        return sorted.sort((a, b) => b.rating - a.rating);
      case 'cadr':
        return sorted.sort((a, b) => b.cadr - a.cadr);
      case 'coverage':
        return sorted.sort((a, b) => b.roomSize - a.roomSize);
      default:
        return sorted;
    }
  };

  const exportRecommendations = () => {
    const data = recommendations.slice(0, 5).map(rec => ({
      Name: rec.name,
      Price: `$${rec.price}`,
      MatchScore: `${rec.score}%`,
      Rating: rec.rating,
      RoomSize: `${rec.roomSize} sq ft`,
      CADR: `${rec.cadr} CFM`,
      YearlyCost: `$${calculateYearlyCost(rec)}`,
      Features: rec.features.join(', ')
    }));

    const csv = [
      Object.keys(data[0]).join(','),
      ...data.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'air-purifier-recommendations.csv';
    a.click();
  };

  const shareResults = () => {
    const text = `Check out my top air purifier recommendations:\n\n${recommendations.slice(0, 3).map((r, i) => `${i+1}. ${r.name} - $${r.price} (${r.score}% match)`).join('\n')}`;
   
    if (navigator.share) {
      navigator.share({
        title: 'Air Purifier Recommendations',
        text: text
      });
    } else {
      navigator.clipboard.writeText(text);
      alert('Results copied to clipboard!');
    }
  };

  // Show admin console if requested
  if (showAdmin) {
    return <AdminConsole onExit={() => setShowAdmin(false)} />;
  }

  return (
    <div className="min-h-screen bg-white p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-700 rounded-full mb-4">
            <Wind className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Air Purifier Finder</h1>
          <p className="text-gray-700">Get personalized recommendations based on your needs</p>
          
          {/* Admin Console Button */}
          <button
            onClick={() => setShowAdmin(true)}
            className="mt-4 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
            title="Admin Console"
          >
            <Settings className="w-4 h-4" />
            Admin
          </button>
        </div>

        {step < 3 && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Step {step} of 2</span>
              <span className="text-sm text-gray-500">
                {step === 1 ? "Basic Info" : "Preferences"}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-700 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(step / 2) * 100}%` }}
              />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Tell us about your space</h2>
           
            <div className="space-y-6">
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Home className="w-4 h-4 mr-2" />
                  Room Size (square feet)
                </label>
                
                {/* Quick select buttons for common room sizes */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                  {[
                    { label: "Small Room", size: 250, desc: "Bedroom" },
                    { label: "Medium Room", size: 400, desc: "Living Room" },
                    { label: "Large Room", size: 800, desc: "Open Space" },
                    { label: "Very Large", size: 1500, desc: "Great Room" }
                  ].map((option) => (
                    <button
                      key={option.size}
                      type="button"
                      onClick={() => handleCriteriaChange('roomSize', option.size.toString())}
                      className={`p-3 border-2 rounded-lg text-left transition-all ${
                        criteria.roomSize === option.size.toString()
                          ? 'border-blue-700 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="font-medium text-sm text-gray-800">{option.label}</div>
                      <div className="text-xs text-gray-600">{option.size} sq ft</div>
                      <div className="text-xs text-gray-500">{option.desc}</div>
                    </button>
                  ))}
                </div>
                
                <input
                  type="number"
                  value={criteria.roomSize}
                  onChange={(e) => handleCriteriaChange('roomSize', e.target.value)}
                  placeholder="Or enter custom size"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                
                {/* Strict Room Size Mode Toggle - Hidden (always enabled) */}
                {false && (
                <div className="mt-3 flex items-start">
                  <input
                    type="checkbox"
                    id="strictRoomSize"
                    checked={criteria.strictRoomSize}
                    onChange={(e) => handleCriteriaChange('strictRoomSize', e.target.checked)}
                    className="mt-1 w-4 h-4 text-blue-700 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="strictRoomSize" className="ml-2 text-sm text-gray-700">
                    <span className="font-medium">Strict room size mode</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Show appropriately-sized purifiers (1500+ sq ft rooms see all larger units)
                    </span>
                  </label>
                </div>
                )}
                
                <p className="text-xs text-gray-500 mt-2">
                  Our database includes purifiers for rooms from 250 to 4000+ sq ft
                </p>
              </div>

              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Budget (USD)
                </label>
                
                {/* Quick select buttons for common budgets */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                  {[
                    { label: "Budget", amount: 200, desc: "Entry Level" },
                    { label: "Mid-Range", amount: 400, desc: "Popular Choice" },
                    { label: "Premium", amount: 700, desc: "High-End" },
                    { label: "Luxury", amount: 1000, desc: "Top-Tier" }
                  ].map((option) => (
                    <button
                      key={option.amount}
                      type="button"
                      onClick={() => handleCriteriaChange('budget', option.amount.toString())}
                      className={`p-3 border-2 rounded-lg text-left transition-all ${
                        criteria.budget === option.amount.toString()
                          ? 'border-blue-700 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="font-medium text-sm text-gray-800">{option.label}</div>
                      <div className="text-xs text-gray-600">${option.amount}</div>
                      <div className="text-xs text-gray-500">{option.desc}</div>
                    </button>
                  ))}
                </div>
                
                <input
                  type="number"
                  value={criteria.budget}
                  onChange={(e) => handleCriteriaChange('budget', e.target.value)}
                  placeholder="Or enter custom budget"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                
                {/* Strict Budget Mode Toggle - Hidden (always enabled) */}
                {false && (
                <div className="mt-3 flex items-start">
                  <input
                    type="checkbox"
                    id="strictBudget"
                    checked={criteria.strictBudget}
                    onChange={(e) => handleCriteriaChange('strictBudget', e.target.checked)}
                    className="mt-1 w-4 h-4 text-blue-700 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="strictBudget" className="ml-2 text-sm text-gray-700">
                    <span className="font-medium">Strict budget mode</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Only show products in my budget range (50%-110% of budget)
                    </span>
                  </label>
                </div>
                )}
                
                <p className="text-xs text-gray-500 mt-2">
                  Our database includes purifiers from $100 to $1000+
                </p>
              </div>

              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  What are your main concerns? (Select all that apply)
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {concerns.map(concern => (
                    <button
                      key={concern.id}
                      onClick={() => toggleArrayValue('concerns', concern.id)}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        criteria.concerns.includes(concern.id)
                          ? 'border-blue-700 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-2xl mr-3">{concern.icon}</span>
                      <span className="font-medium text-gray-800">{concern.label}</span>
                    </button>
                  ))}
                </div>
                
                {/* Strict Concerns Mode Toggle - Hidden (always enabled) */}
                {false && (
                <div className="mt-3 flex items-start">
                  <input
                    type="checkbox"
                    id="strictConcerns"
                    checked={criteria.strictConcerns}
                    onChange={(e) => handleCriteriaChange('strictConcerns', e.target.checked)}
                    className="mt-1 w-4 h-4 text-blue-700 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="strictConcerns" className="ml-2 text-sm text-gray-700">
                    <span className="font-medium">Strict concerns mode</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Only show products that address at least one of my concerns
                    </span>
                  </label>
                </div>
                )}
              </div>

              <button
                onClick={() => setStep(2)}
                disabled={!criteria.roomSize || !criteria.budget || criteria.concerns.length === 0}
                className="w-full bg-blue-700 text-white py-3 rounded-lg font-medium hover:bg-blue-800 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
              >
                Continue <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Additional preferences</h2>
           
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-3 block">
                  Important features (optional)
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {features.map(feature => (
                    <button
                      key={feature.id}
                      onClick={() => toggleArrayValue('features', feature.id)}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        criteria.features.includes(feature.id)
                          ? 'border-blue-700 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-2xl mr-3">{feature.icon}</span>
                      <span className="font-medium text-gray-800">{feature.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={generateRecommendations}
                  className="flex-1 bg-blue-700 text-white py-3 rounded-lg font-medium hover:bg-blue-800 flex items-center justify-center transition-colors"
                >
                  Get Recommendations <ArrowRight className="w-5 h-5 ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">Your Recommendations</h2>
                  <p className="text-gray-600">
                    Found {recommendations.length} air purifier{recommendations.length !== 1 ? 's' : ''} matching your criteria
                    {recommendations.length < 6 && recommendations.length > 0 && (
                      <span className="text-blue-700 font-medium"> • Showing best matches</span>
                    )}
                  </p>
                  {/* Show selected concerns */}
                  {criteria.concerns.length > 0 && (
                    <div className="mt-2">
                      <span className="text-sm text-gray-700 font-medium">Your concerns: </span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {criteria.concerns.map((concernId) => {
                          const concern = concerns.find(c => c.id === concernId);
                          return concern ? (
                            <span key={concernId} className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                              {concern.icon} {concern.label}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Show selected additional preferences */}
                  {criteria.features.length > 0 && (
                    <div className="mt-2">
                      <span className="text-sm text-gray-700 font-medium">Additional preferences: </span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {criteria.features.map((featureId) => {
                          const feature = features.find(f => f.id === featureId);
                          return feature ? (
                            <span key={featureId} className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full">
                              {feature.icon} {feature.label}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Show active strict filters */}
                  {(criteria.strictRoomSize || criteria.strictBudget || criteria.strictConcerns) && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {criteria.strictRoomSize && criteria.roomSize && (
                        <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full">
                          {parseInt(criteria.roomSize) >= 1500 
                            ? `Room ${criteria.roomSize}+ sq ft`
                            : `Room ${criteria.roomSize}-${Math.round(parseInt(criteria.roomSize) * 1.5)} sq ft`
                          }
                        </span>
                      )}
                      {criteria.strictBudget && criteria.budget && (
                        <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full">
                          Budget ${Math.round(parseInt(criteria.budget) * 0.5)}-${Math.round(parseInt(criteria.budget) * 1.1)}
                        </span>
                      )}
                      {criteria.strictConcerns && criteria.concerns.length > 0 && (
                        <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full">
                          Must match concerns
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={shareResults}
                    className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium hover:bg-blue-200"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={exportRecommendations}
                    className="px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm font-medium hover:bg-green-200"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
             
              {compareList.length > 0 && (
                <div className="flex items-center justify-between bg-blue-50 p-4 rounded-lg">
                  <span className="font-medium text-gray-800">{compareList.length} selected</span>
                  <button
                    onClick={() => setShowComparison(true)}
                    className="bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-800"
                  >
                    Compare
                  </button>
                </div>
              )}
              
              {/* Pagination Info */}
              {recommendations.length > 0 && (
                <div className="text-sm text-gray-600 text-center">
                  Showing {Math.min((currentPage - 1) * resultsPerPage + 1, recommendations.length)}-{Math.min(currentPage * resultsPerPage, recommendations.length)} of {recommendations.length} results
                </div>
              )}
            </div>

            {(() => {
              const sorted = sortRecommendations(recommendations);
              const startIndex = (currentPage - 1) * resultsPerPage;
              const endIndex = startIndex + resultsPerPage;
              const paginatedResults = sorted.slice(startIndex, endIndex);
              
              return paginatedResults.map((rec, index) => {
                const globalIndex = startIndex + index; // For "Best Match" badge
                return (
              <div key={rec.name} className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex justify-between mb-4">
                  <div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {globalIndex === 0 && sortBy === 'match' && (
                        <span className="bg-green-100 text-green-800 text-xs px-3 py-1 rounded-full font-semibold">
                          Best Match
                        </span>
                      )}
                      <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                        rec.score >= 70 ? 'bg-green-100 text-green-800' :
                        rec.score >= 50 ? 'bg-blue-100 text-blue-800' :
                        'bg-orange-100 text-orange-800'
                      }`}>
                        {rec.score}% Match
                      </span>
                      {/* Show matching concerns */}
                      {criteria.concerns.length > 0 && criteria.concerns.filter(c => rec.bestFor.includes(c)).map(concern => {
                        const concernInfo = concerns.find(co => co.id === concern);
                        return (
                          <span key={concern} className="bg-blue-50 text-blue-700 text-xs px-3 py-1 rounded-full font-medium">
                            {concernInfo?.icon} {concernInfo?.label}
                          </span>
                        );
                      })}
                      {/* Show matching additional preferences */}
                      {criteria.features.length > 0 && (() => {
                        const rawFeatures = rec._rawFeatures || {};
                        const matchingFeatures = criteria.features.filter(featureId => {
                          if (featureId === 'smart' && rawFeatures.smart_features?.app_control === true) return true;
                          if (featureId === 'quiet' && rawFeatures.quiet_operation === true) return true;
                          if (featureId === 'low-maintenance' && rawFeatures.maintenance?.easy_maintenance === true) return true;
                          if (featureId === 'energy-efficient' && rawFeatures.energy_star_certified === true) return true;
                          return false;
                        });
                        return matchingFeatures.map(featureId => {
                          const feature = features.find(f => f.id === featureId);
                          return feature ? (
                            <span key={featureId} className="bg-yellow-50 text-yellow-700 text-xs px-3 py-1 rounded-full font-medium">
                              {feature.icon} {feature.label}
                            </span>
                          ) : null;
                        });
                      })()}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{rec.name}</h3>
                    <p className="text-gray-600 text-sm mt-1">{rec.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-800">${rec.price}</p>
                    <p className="text-xs text-gray-500">${calculateYearlyCost(rec)}/yr</p>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600">Coverage</p>
                    <p className="text-sm font-semibold">{rec.roomSize} sq ft</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600">CADR</p>
                    <p className="text-sm font-semibold">{rec.cadr}</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600">Power</p>
                    <p className="text-sm font-semibold">{rec.energyUsage}W</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600">Noise</p>
                    <p className="text-sm font-semibold capitalize">{rec.noiseLevel}</p>
                  </div>
                </div>

                <div className="flex gap-2">
                  {rec.amazonLink ? (
                    <a
                      href={rec.amazonLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-blue-700 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-800 text-center"
                    >
                      View on Amazon
                    </a>
                  ) : (
                    <button
                      onClick={() => setSelectedProduct(rec)}
                      className="flex-1 bg-blue-700 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-800"
                    >
                      View Details
                    </button>
                  )}
                  <button
                    onClick={() => toggleCompare(rec)}
                    disabled={compareList.length >= 3 && !compareList.find(p => p.name === rec.name)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      compareList.find(p => p.name === rec.name)
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    Compare
                  </button>
                </div>
              </div>
                );
              });
            })()}

            {/* Pagination Controls */}
            {recommendations.length > resultsPerPage && (
              <div className="bg-white rounded-lg shadow-lg p-4">
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Start
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Back
                  </button>
                  <span className="px-4 text-sm font-medium text-gray-700">
                    Page {currentPage} of {Math.ceil(recommendations.length / resultsPerPage)}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(Math.ceil(recommendations.length / resultsPerPage), prev + 1))}
                    disabled={currentPage === Math.ceil(recommendations.length / resultsPerPage)}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.ceil(recommendations.length / resultsPerPage))}
                    disabled={currentPage === Math.ceil(recommendations.length / resultsPerPage)}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    End
                  </button>
                </div>
              </div>
            )}

            <button
              onClick={resetAgent}
              className="w-full bg-gray-200 text-gray-800 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              Start Over
            </button>
          </div>
        )}

        {selectedProduct && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b p-6 flex justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">{selectedProduct.name}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-3xl font-bold text-blue-800">${selectedProduct.price}</span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Description</h4>
                  <p className="text-gray-600">{selectedProduct.detailedReview}</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Specifications</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600">Room Coverage</p>
                      <p className="font-semibold text-gray-800">{selectedProduct.roomSize} sq ft</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600">CADR Rating</p>
                      <p className="font-semibold text-gray-800">{selectedProduct.cadr} CFM</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600">Noise Level</p>
                      <p className="font-semibold text-gray-800 capitalize">{selectedProduct.noiseLevel}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600">Energy Usage</p>
                      <p className="font-semibold text-gray-800">{selectedProduct.energyUsage}W</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600">Filter Lifespan</p>
                      <p className="font-semibold text-gray-800">{selectedProduct.filterLifespan}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600">Warranty</p>
                      <p className="font-semibold text-gray-800">{selectedProduct.warranty}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Cost Analysis</h4>
                  <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Purchase Price:</span>
                      <span className="font-semibold">${selectedProduct.price}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Yearly Cost:</span>
                      <span className="font-semibold">${calculateYearlyCost(selectedProduct)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-blue-200">
                      <span className="font-medium">5-Year Total:</span>
                      <span className="font-bold text-blue-800">${selectedProduct.price + (calculateYearlyCost(selectedProduct) * 5)}</span>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Pros</h4>
                    <ul className="space-y-1">
                      {selectedProduct.pros.map((pro, i) => (
                        <li key={i} className="text-sm text-gray-600 flex items-start">
                          <span className="text-green-600 mr-2">✓</span>
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Cons</h4>
                    <ul className="space-y-1">
                      {selectedProduct.cons.map((con, i) => (
                        <li key={i} className="text-sm text-gray-600 flex items-start">
                          <span className="text-orange-600 mr-2">•</span>
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showComparison && compareList.length > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b p-6 flex justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Compare Air Purifiers</h3>
                  <p className="text-gray-600 text-sm mt-1">Comparing {compareList.length} products</p>
                </div>
                <button
                  onClick={() => setShowComparison(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2">
                      <th className="text-left p-4 font-semibold">Feature</th>
                      {compareList.map((product, i) => (
                        <th key={i} className="p-4 text-center">
                          <div className="font-bold">{product.name}</div>
                          <div className="text-2xl font-bold text-blue-800">${product.price}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="p-4 font-medium">Match Score</td>
                      {compareList.map((product, i) => (
                        <td key={i} className="p-4 text-center">
                          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                            {product.score}%
                          </span>
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b bg-gray-50">
                      <td className="p-4 font-medium">Room Coverage</td>
                      {compareList.map((product, i) => (
                        <td key={i} className="p-4 text-center font-semibold">{product.roomSize} sq ft</td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="p-4 font-medium">CADR</td>
                      {compareList.map((product, i) => (
                        <td key={i} className="p-4 text-center font-semibold">{product.cadr} CFM</td>
                      ))}
                    </tr>
                    <tr className="border-b bg-gray-50">
                      <td className="p-4 font-medium">Noise Level</td>
                      {compareList.map((product, i) => (
                        <td key={i} className="p-4 text-center capitalize">{product.noiseLevel}</td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="p-4 font-medium">Energy Usage</td>
                      {compareList.map((product, i) => (
                        <td key={i} className="p-4 text-center">{product.energyUsage}W</td>
                      ))}
                    </tr>
                    <tr className="border-b bg-gray-50">
                      <td className="p-4 font-medium">Filter Cost</td>
                      {compareList.map((product, i) => (
                        <td key={i} className="p-4 text-center">${product.filterCost}</td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="p-4 font-medium">Warranty</td>
                      {compareList.map((product, i) => (
                        <td key={i} className="p-4 text-center font-medium">{product.warranty}</td>
                      ))}
                    </tr>
                    <tr className="border-b bg-blue-50">
                      <td className="p-4 font-medium">Yearly Cost</td>
                      {compareList.map((product, i) => (
                        <td key={i} className="p-4 text-center font-semibold">${calculateYearlyCost(product)}</td>
                      ))}
                    </tr>
                    <tr className="bg-blue-50">
                      <td className="p-4 font-medium">5-Year Total</td>
                      {compareList.map((product, i) => (
                        <td key={i} className="p-4 text-center font-bold text-blue-800">
                          ${product.price + (calculateYearlyCost(product) * 5)}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>

                <div className="mt-6 flex gap-3">
                  <button
                    onClick={() => setShowComparison(false)}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg font-medium hover:bg-gray-300"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setCompareList([]);
                      setShowComparison(false);
                    }}
                    className="flex-1 bg-blue-700 text-white py-3 rounded-lg font-medium hover:bg-blue-800"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AirPurifierAgent;